import * as React from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';

import { Image, Typography } from '../../../../atoms';
import { NavList } from '../../../../molecules';
import { NavListItemProps } from '../../../../molecules/NavListItem/NavListItem';

type RouteParams = {
  concessionStoreId?: number;
  id?: number;
  type?: 'collection' | 'delivery';
  vrid: number;
};

type Props = {
  concessionStoreList: any;
  params: RouteParams;
  translate: any;
};

const useStyles = makeStyles((theme: Theme) => ({
  concessionStoreNavList: {
    '& a': {
      padding: theme.spacing(2),
    },
  },

  closed: {
    '&.fd-text em': {
      color: theme.palette.error.main,
      fontWeight: theme.typography.fontWeightRegular,
    },
    '&.fd-text span': {
      color: theme.palette.grey[600],
    },
  },

  image: {
    height: '64px',
    marginRight: theme.spacing(2),
    width: '64px',
  },

  storeInfo: {
    alignItems: 'center',
    display: 'flex',
    minHeight: '64px',

    '& .MuiTypography-body1': {
      marginBottom: theme.spacing(0.25),
    },
    '& picture, & img': {
      display: 'block',
    },
  },

  [theme.breakpoints.up('sm')]: {
    concessionStoreNavList: {
      '& a': {
        padding: theme.spacing(2, 3),
      },
    },
  },

  [theme.breakpoints.up('md')]: {
    concessionStoreNavList: {
      '& a': {
        padding: theme.spacing(2, 0),
      },
    },
  },
}));

export function ConcessionStoreNavList(props: Props): JSX.Element | null {
  const { concessionStoreList, params, translate } = props;

  const classes = useStyles();

  const renderClosedMessage = (store): JSX.Element => {
    const closedWarningMessage: TranslationId = store.isOpeningTimeTomorrow
      ? 'StoreMenu_ConcessionStoreNavList_UntilTomorrowWarning'
      : 'StoreMenu_ConcessionStoreNavList_UntilWarning';

    return (
      <span className={classes.closed}>
        <em>{translate('StoreMenu_ConcessionNav_StoreClosedWarning')}</em>
        {store.openingTime &&
          ` ${translate(closedWarningMessage, {
            openingTime: store.openingTime,
          })}`}
      </span>
    );
  };

  const renderConcessionList = (): NavListItemProps[] => {
    const concessionList = concessionStoreList.map(
      (store): NavListItemProps => {
        const concessionPath = `/restaurant/${params.vrid}/${params.type}/${params.id}/${store.ConcessionStoreId}`;
        const { isDisabled } = store;

        const concessionNavItem = (
          <div
            className={classes.storeInfo}
            key={`store-${params.concessionStoreId}`}
          >
            {store.ImageUrl && (
              <Image
                alt={store.Name}
                className={classes.image}
                dimensions={[64, 64]}
                src={store.ImageUrl}
              />
            )}
            <div>
              <Typography>
                <em>{store.Name}</em>
              </Typography>

              <Typography variant="body2-secondary">
                {store.Description}
              </Typography>

              {store.isDisabled && (
                <Typography
                  className={classes.closed}
                  variant="body2-secondary"
                >
                  {renderClosedMessage(store)}
                </Typography>
              )}
            </div>
          </div>
        );

        return {
          disabled: isDisabled,
          elements: [concessionNavItem],
          linkId: `concession-${store.ConcessionStoreId}`,
          linkPath: !isDisabled ? concessionPath : '',
          variant: isDisabled ? 'disabled' : 'default',
        };
      },
    );

    return concessionList;
  };

  return (
    <NavList
      className={classes.concessionStoreNavList}
      items={renderConcessionList()}
    />
  );
}
